import React, { FC } from 'react';

import { EvContactSupportEmailLink, EVINCED_SUPPORT_EMAIL } from '@evinced-private/ui-common';

import { ApiErrorHelper } from '../../helpers/ApiErrorHelper';
import { IServerError } from '../../interfaces/ServerError';
import { logger } from '../../services/Logger';
import { UnallowedDomainError } from '../AllowedDomainsError';
import { SiteScannerPopup } from '../common/site-scanner-popup/SiteScannerPopup';

import './AlertPopup.scss';

export interface IAlertPopup {
	/**
	 * Error message to display
	 */
	errorMessage?: string;

	/**
	 * popup's title
	 */
	title?: string;

	/**
	 * Server error object
	 */
	serverError?: IServerError;

	/** popup is shown only if isOpened==true */
	isOpened?: boolean;

	/**
	 * A callback function to be called when user hits OK to close the popup.
	 * The function will be called after the popup is closed.
	 */
	onOK?: () => void | Promise<void>;
}

export const AlertPopup: FC<IAlertPopup> = ({
	title = 'Something went wrong',
	errorMessage,
	onOK,
	serverError,
	isOpened = false
}: IAlertPopup) => {
	const buttons = [
		{
			title: 'OK',
			onClick: () => {
				logger.info('Error popup closed on OK click');
				onOK();
			}
		}
	];

	const renderErrorTitle = (): JSX.Element => <div className="error-title">{title}</div>;

	const renderErrorMessage = (): JSX.Element => {
		if (!errorMessage) {
			return null;
		}
		return <div className="error-message">{errorMessage}</div>;
	};

	const renderServerErrorDetails = (): JSX.Element => {
		const serverErrorMessage = ApiErrorHelper.getErrorMessage(serverError);
		if (!serverErrorMessage) {
			return null;
		}

		const isErrorRelatedToDomains = ApiErrorHelper.isErrorRelatedToDomains(serverError);
		if (isErrorRelatedToDomains) {
			return (
				<div className="error-details">
					<UnallowedDomainError serverError={serverError} />
				</div>
			);
		}

		if (serverErrorMessage.includes(EVINCED_SUPPORT_EMAIL)) {
			const splitMessage = serverErrorMessage.split(EVINCED_SUPPORT_EMAIL);
			return (
				<div className="error-details">
					{splitMessage[0] || ''}
					<EvContactSupportEmailLink />
					{splitMessage[1] || ''}
				</div>
			);
		}

		return <div className="error-details">{serverErrorMessage}</div>;
	};

	const isClientError = ApiErrorHelper.isClientError(serverError);

	return (
		<SiteScannerPopup
			title={title}
			isSrOnly={!isClientError}
			buttons={buttons}
			isControlled
			isCentered
			isOpen={isOpened}
			className="error-popup"
			onClose={onOK}
			popupWidth="fit-content"
		>
			<div className="alert-popup">
				{!isClientError && renderErrorTitle()}
				{renderErrorMessage()}
				{renderServerErrorDetails()}
			</div>
		</SiteScannerPopup>
	);
};
